/**
 * Callback-inbox detail (PR-6a + PR-6b actions).
 *
 * Surfaces the metadata + headers + requeue-audit history. PR-6b
 * adds two actions:
 *   - "Re-queue" button on dead-lettered rows (reason required).
 *   - "Reveal raw bytes" on any row (step-up TOTP + reason required).
 * Both flows use the shared :class:`ActionConfirmDialog` modal.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Eye, RotateCw } from "lucide-react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { JsonBlock } from "@/components/payments/JsonBlock";
import { ActionConfirmDialog } from "@/components/observability/ActionConfirmDialog";
import {
  asApiError,
  getCallbackInboxRow,
  requeueCallbackInbox,
  revealCallbackInboxBody,
  type CallbackInboxDetail,
  type CallbackInboxRevealBodyResponse,
  type CallbackInboxState,
} from "@/lib/api";

const STATE_VARIANT: Record<CallbackInboxState, BadgeProps["variant"]> = {
  pending: "info",
  processed: "success",
  dead_lettered: "destructive",
};

export function CallbackInboxDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ["callback-inbox", id] as const,
    queryFn: () => getCallbackInboxRow(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load this inbox row", {
        description: "It may not exist or you may not have access.",
      });
    }
  }, [query.error]);

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div>
            <CardTitle className="text-base">Couldn't load this inbox row</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              It may not exist or you may not have access.{" "}
              <Link to="/callback-inbox" className="text-foreground underline">
                Back to list
              </Link>
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  }
  return <Loaded row={query.data} />;
}

function Loaded({ row }: { row: CallbackInboxDetail }) {
  const queryClient = useQueryClient();
  const [requeueOpen, setRequeueOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealResult, setRevealResult] =
    useState<CallbackInboxRevealBodyResponse | null>(null);
  const [requeueError, setRequeueError] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);

  const requeueMutation = useMutation({
    mutationFn: (vars: { reason: string }) =>
      requeueCallbackInbox(row.id, { reason: vars.reason }),
    onSuccess: () => {
      toast.success("Row re-queued", {
        description: "The worker will pick it up on the next tick.",
      });
      setRequeueOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["callback-inbox", row.id] });
    },
    onError: (err: unknown) => {
      const body = asApiError(err);
      setRequeueError(body?.detail ?? "Couldn't re-queue this row.");
    },
  });

  const revealMutation = useMutation({
    mutationFn: (vars: { reason: string; totpCode: string }) =>
      revealCallbackInboxBody(row.id, {
        totp_code: vars.totpCode,
        reason: vars.reason,
      }),
    onSuccess: (data) => {
      setRevealResult(data);
      setRevealOpen(false);
      toast.success("Raw body revealed", {
        description: "An audit row has been written.",
      });
    },
    onError: (err: unknown) => {
      const body = asApiError(err);
      if (body?.error === "invalid_code") {
        setRevealError("That TOTP code didn't verify. Try the next one.");
      } else if (body?.error === "account_locked") {
        setRevealError("Account locked. Wait and try again.");
      } else if (body?.error === "totp_not_enrolled") {
        setRevealError("Your account doesn't have TOTP enrolled.");
      } else if (body?.error === "audit_unavailable") {
        // Backend refused to reveal because the audit row couldn't be
        // written (PR-6b review #1: forensic guarantee). Retry tends
        // to succeed once the DB blip clears.
        setRevealError(
          "Audit log is unavailable right now — reveal aborted to preserve the forensic trail. Retry shortly.",
        );
      } else {
        setRevealError(body?.detail ?? "Couldn't reveal the raw bytes.");
      }
    },
  });

  return (
    <>
      <div className="mb-6 space-y-3">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 h-auto px-2 text-muted-foreground"
        >
          <Link to="/callback-inbox">
            <ArrowLeft />
            Back to callback inbox
          </Link>
        </Button>

        <Card className="p-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-lg font-semibold">{row.plugin}</span>
                <Badge variant={STATE_VARIANT[row.state]} className="capitalize">
                  {row.state.replace(/_/g, " ")}
                </Badge>
                {row.provider_account ? (
                  <Badge variant="muted" className="capitalize">
                    {row.provider_account.tenant.environment}
                  </Badge>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                {row.provider_account ? (
                  <>
                    <span className="font-medium text-foreground">
                      {row.provider_account.tenant.name}
                    </span>
                    <span>·</span>
                    <span>{row.provider_account.name}</span>
                    <span>·</span>
                  </>
                ) : (
                  <span className="italic">unresolved account</span>
                )}
                <span title={row.received_at}>
                  received {new Date(row.received_at).toLocaleString()}
                </span>
              </div>
              <code className="block truncate font-mono text-xs text-muted-foreground/80">
                {row.id}
              </code>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {row.state === "dead_lettered" ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRequeueError(null);
                    setRequeueOpen(true);
                  }}
                >
                  <RotateCw />
                  Re-queue
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setRevealError(null);
                  setRevealOpen(true);
                }}
              >
                <Eye />
                Reveal raw bytes
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="headers">Headers</TabsTrigger>
          <TabsTrigger value="audits">
            Re-queue audits ({row.requeue_audits.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Identity</CardTitle>
              </CardHeader>
              <CardContent>
                <Defs>
                  <Def label="Inbox ID" mono>
                    {row.id}
                  </Def>
                  <Def label="Plugin">{row.plugin}</Def>
                  <Def label="Dedup key" mono>
                    {row.dedup_key}
                  </Def>
                  <Def label="Attempts">{row.attempts}</Def>
                </Defs>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Timing + state</CardTitle>
              </CardHeader>
              <CardContent>
                <Defs>
                  <Def label="Received">
                    {new Date(row.received_at).toLocaleString()}
                  </Def>
                  <Def label="Processed">
                    {row.processed_at
                      ? new Date(row.processed_at).toLocaleString()
                      : "—"}
                  </Def>
                  <Def label="Processing error">
                    {row.processing_error ? (
                      <code className="break-all font-mono text-xs">
                        {row.processing_error}
                      </code>
                    ) : (
                      "—"
                    )}
                  </Def>
                </Defs>
              </CardContent>
            </Card>
          </div>

          {revealResult ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Revealed raw body</CardTitle>
                <p className="text-xs text-muted-foreground">
                  {revealResult.byte_length.toLocaleString()} bytes. An audit
                  row has been written; the reveal will appear in the
                  super-admin audit log indexed by your operator id.
                </p>
              </CardHeader>
              <CardContent>
                {revealResult.body_text !== null ? (
                  <pre
                    data-testid="revealed-body"
                    className="scrollbar-thin max-h-96 overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-xs leading-relaxed"
                  >
                    {revealResult.body_text}
                  </pre>
                ) : (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Bytes were not valid UTF-8; rendering as base64.
                    </p>
                    <code
                      data-testid="revealed-body-base64"
                      className="block max-h-96 overflow-auto break-all rounded-md border bg-muted/30 p-3 font-mono text-xs"
                    >
                      {revealResult.body_base64}
                    </code>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Raw body</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Use <b>Reveal raw bytes</b> above to decrypt and display the
                  provider's original callback body. Each reveal requires a
                  fresh TOTP code + a reason, and is recorded in the admin
                  audit log per ADR-0014.
                </p>
              </CardHeader>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="headers">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Request headers</CardTitle>
              <p className="text-xs text-muted-foreground">
                Headers as captured at the HTTP handler. Includes
                content-type, signature header (where supplied), and any
                provider-supplied tracing identifiers.
              </p>
            </CardHeader>
            <CardContent>
              <JsonBlock value={row.raw_headers} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audits">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Re-queue audit log</CardTitle>
              <p className="text-xs text-muted-foreground">
                Append-only history of re-queues performed against this row.
                Each entry captures who, why, and the state the row was in
                before re-queue.
              </p>
            </CardHeader>
            <CardContent>
              {row.requeue_audits.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No re-queues have been performed on this row.
                </p>
              ) : (
                <ul className="space-y-3">
                  {row.requeue_audits.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-md border bg-muted/20 px-3 py-2 text-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium">{a.actor}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(a.requeued_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-1 text-muted-foreground">{a.reason}</div>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span>prior attempts: {a.prior_attempts}</span>
                        {a.prior_processing_error ? (
                          <code className="break-all font-mono">
                            {a.prior_processing_error}
                          </code>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ActionConfirmDialog
        open={requeueOpen}
        onOpenChange={(open) => {
          setRequeueOpen(open);
          if (!open) setRequeueError(null);
        }}
        title="Re-queue this callback"
        description={
          "The row's processed_at + processing_error will be cleared so the worker picks it up again. " +
          "A reason is required and will be captured in both the per-row audit log and the admin audit log."
        }
        confirmLabel="Re-queue"
        pending={requeueMutation.isPending}
        error={requeueError}
        onSubmit={({ reason }) => requeueMutation.mutate({ reason })}
      />

      <ActionConfirmDialog
        open={revealOpen}
        onOpenChange={(open) => {
          setRevealOpen(open);
          if (!open) setRevealError(null);
        }}
        title="Reveal raw callback bytes"
        description={
          "The bytes may contain PII (MSISDN, payer name, free text) or partial credentials. " +
          "Step-up TOTP is required; the reveal will be logged in the super-admin audit log with the reason you provide."
        }
        confirmLabel="Reveal"
        confirmVariant="destructive"
        requireTotp
        pending={revealMutation.isPending}
        error={revealError}
        onSubmit={({ reason, totpCode }) =>
          revealMutation.mutate({ reason, totpCode })
        }
      />
    </>
  );
}

function Defs({ children }: { children: React.ReactNode }) {
  return <dl className="space-y-3">{children}</dl>;
}

function Def({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd
        className={
          mono
            ? "min-w-0 break-all font-mono text-xs sm:text-right"
            : "min-w-0 text-sm sm:text-right"
        }
      >
        {children}
      </dd>
    </div>
  );
}

export default CallbackInboxDetailPage;
